import { useEffect } from "react";

const IDLE_MS = 1200;
const THUMB_THICKNESS = 8;
const THUMB_MIN_LEN = 24;
const EDGE_INSET = 2;
const HIT_PAD = 3;
const SWEEP_MS = 5000;

type Axis = "v" | "h";

interface ThumbRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface Tracked {
  vThumb: HTMLDivElement | null;
  hThumb: HTMLDivElement | null;
  vRect: ThumbRect | null;
  hRect: ThumbRect | null;
  resizeObserver: ResizeObserver;
  idleTimer: ReturnType<typeof setTimeout> | null;
}

interface DragState {
  container: Element;
  axis: Axis;
  startClient: number;
  startScroll: number;
  trackLen: number;
  thumbLen: number;
  scrollRange: number;
}

/** Custom overlay scrollbars for desktop (`pointer: fine`) browsers.
 *
 *  Firefox's native scrollbar chrome isn't a stylable box — `scrollbar-color`
 *  cannot be transitioned there at all, so an idle fade is impossible with the
 *  real scrollbar. Instead we hide the native one (see the `@media (pointer:
 *  fine)` block in globals.css) and draw + fade our own thumb, positioned via
 *  `getBoundingClientRect` against a `position: fixed` div per scrolling
 *  element found lazily (on scroll/hover), not enumerated upfront.
 *
 *  The thumb floats OVER content (no reserved gutter), so it is
 *  `pointer-events: none` by default and only becomes grabbable (`.is-interactive`)
 *  while the pointer is actually over it — otherwise the overlay would swallow
 *  clicks meant for content near the container's edge. Hover reveal is skipped
 *  entirely while any mouse button is held (`e.buttons`), so a drag/resize
 *  elsewhere in the app never pays for our per-frame scrollable-ancestor walk.
 *
 *  Mobile/touch is untouched — those platforms already auto-hide their native
 *  overlay scrollbar, and a pointer-events-capturing drag thumb would risk
 *  fighting the touch gesture handling used elsewhere (calendar drag, sidebar
 *  reorder, subtask nesting). */
export function useOverlayScrollbars() {
  useEffect(() => {
    if (!window.matchMedia("(pointer: fine)").matches) return;

    const tracked = new Map<Element, Tracked>();
    let dragging: DragState | null = null;

    const removeTracked = (container: Element) => {
      const state = tracked.get(container);
      if (!state) return;
      state.resizeObserver.disconnect();
      state.vThumb?.remove();
      state.hThumb?.remove();
      if (state.idleTimer) clearTimeout(state.idleTimer);
      tracked.delete(container);
    };

    const updateGeometry = (container: Element) => {
      const state = tracked.get(container);
      if (!state) return;
      if (!document.contains(container)) {
        removeTracked(container);
        return;
      }
      const rect = container.getBoundingClientRect();
      const needsV = container.scrollHeight > container.clientHeight + 1;
      const needsH = container.scrollWidth > container.clientWidth + 1;

      if (needsV) {
        const thumb = (state.vThumb ??= makeThumb("v", container, state));
        const trackLen = rect.height - EDGE_INSET * 2;
        const thumbLen = Math.max(THUMB_MIN_LEN, (container.clientHeight / container.scrollHeight) * trackLen);
        const maxScroll = container.scrollHeight - container.clientHeight;
        const frac = maxScroll > 0 ? container.scrollTop / maxScroll : 0;
        const top = rect.top + EDGE_INSET + frac * (trackLen - thumbLen);
        const left = rect.right - THUMB_THICKNESS - EDGE_INSET;
        thumb.style.top = `${top}px`;
        thumb.style.left = `${left}px`;
        thumb.style.height = `${thumbLen}px`;
        state.vRect = { top, left, width: THUMB_THICKNESS, height: thumbLen };
      } else if (state.vThumb) {
        state.vThumb.remove();
        state.vThumb = null;
        state.vRect = null;
      }

      if (needsH) {
        const thumb = (state.hThumb ??= makeThumb("h", container, state));
        const trackLen = rect.width - EDGE_INSET * 2;
        const thumbLen = Math.max(THUMB_MIN_LEN, (container.clientWidth / container.scrollWidth) * trackLen);
        const maxScroll = container.scrollWidth - container.clientWidth;
        const frac = maxScroll > 0 ? container.scrollLeft / maxScroll : 0;
        const left = rect.left + EDGE_INSET + frac * (trackLen - thumbLen);
        const top = rect.bottom - THUMB_THICKNESS - EDGE_INSET;
        thumb.style.left = `${left}px`;
        thumb.style.top = `${top}px`;
        thumb.style.width = `${thumbLen}px`;
        state.hRect = { top, left, width: thumbLen, height: THUMB_THICKNESS };
      } else if (state.hThumb) {
        state.hThumb.remove();
        state.hThumb = null;
        state.hRect = null;
      }
    };

    const makeThumb = (axis: Axis, container: Element, state: Tracked): HTMLDivElement => {
      const el = document.createElement("div");
      el.className = "scrollbar-overlay-thumb";
      el.style.width = axis === "v" ? `${THUMB_THICKNESS}px` : "0px";
      el.style.height = axis === "h" ? `${THUMB_THICKNESS}px` : "0px";
      el.addEventListener("pointerdown", (e) => startDrag(e, container, axis, state));
      document.body.appendChild(el);
      return el;
    };

    const getTracked = (container: Element): Tracked => {
      let state = tracked.get(container);
      if (!state) {
        const resizeObserver = new ResizeObserver(() => updateGeometry(container));
        resizeObserver.observe(container);
        state = { vThumb: null, hThumb: null, vRect: null, hRect: null, resizeObserver, idleTimer: null };
        tracked.set(container, state);
      }
      return state;
    };

    const activate = (container: Element) => {
      if (container.classList.contains("no-scrollbar")) return;
      const scrollable = container.scrollHeight > container.clientHeight + 1 || container.scrollWidth > container.clientWidth + 1;
      if (!scrollable) return;
      const state = getTracked(container);
      updateGeometry(container);
      state.vThumb?.classList.add("is-visible");
      state.hThumb?.classList.add("is-visible");
      if (state.idleTimer) clearTimeout(state.idleTimer);
      state.idleTimer = setTimeout(() => {
        state.vThumb?.classList.remove("is-visible");
        state.hThumb?.classList.remove("is-visible");
      }, IDLE_MS);
    };

    const refreshVisible = () => {
      for (const [container, state] of tracked) {
        if (state.vThumb?.classList.contains("is-visible") || state.hThumb?.classList.contains("is-visible")) {
          updateGeometry(container);
        }
      }
    };

    const findScrollable = (target: EventTarget | null): Element | null => {
      if (!(target instanceof Element)) return null;
      let el: Element | null = target;
      while (el && el !== document.body) {
        if (!el.classList.contains("no-scrollbar")) {
          const style = getComputedStyle(el);
          const canScrollY = (style.overflowY === "auto" || style.overflowY === "scroll") && el.scrollHeight > el.clientHeight + 1;
          const canScrollX = (style.overflowX === "auto" || style.overflowX === "scroll") && el.scrollWidth > el.clientWidth + 1;
          if (canScrollY || canScrollX) return el;
        }
        el = el.parentElement;
      }
      return null;
    };

    // Reuse the last walk result while the pointer dwells on one element — a
    // fresh `findScrollable` per frame forces a synchronous style/layout flush
    // (getComputedStyle + scrollHeight up the tree) on every mouse move.
    let cacheTarget: Element | null = null;
    let cacheResult: Element | null = null;
    const findScrollableCached = (target: EventTarget | null): Element | null => {
      if (target instanceof Element && target === cacheTarget) return cacheResult;
      const result = findScrollable(target);
      cacheTarget = target instanceof Element ? target : null;
      cacheResult = result;
      return result;
    };

    const pointOverRect = (r: ThumbRect, x: number, y: number): boolean =>
      x >= r.left - HIT_PAD && x <= r.left + r.width + HIT_PAD && y >= r.top - HIT_PAD && y <= r.top + r.height + HIT_PAD;

    /** Toggle `.is-interactive` (→ pointer-events: auto) on whichever visible
     *  thumb the pointer is over, clearing it on the rest. Returns the container
     *  of the hovered thumb, so the caller can keep it awake while the user
     *  reaches for it. Uses cached geometry only — no layout reads. */
    const updateThumbInteractivity = (x: number, y: number): Element | null => {
      let hovered: Element | null = null;
      for (const [container, state] of tracked) {
        const vOver = !!state.vRect && !!state.vThumb?.classList.contains("is-visible") && pointOverRect(state.vRect, x, y);
        const hOver = !!state.hRect && !!state.hThumb?.classList.contains("is-visible") && pointOverRect(state.hRect, x, y);
        state.vThumb?.classList.toggle("is-interactive", vOver);
        state.hThumb?.classList.toggle("is-interactive", hOver);
        if (vOver || hOver) hovered = container;
      }
      return hovered;
    };

    const handleScroll = (e: Event) => {
      if (dragging) return;
      if (e.target instanceof Element) activate(e.target);
      refreshVisible();
    };

    let hoverRaf = 0;
    const handlePointerMove = (e: PointerEvent) => {
      if (dragging) return;
      if (e.buttons !== 0) return; // a drag/resize is in progress somewhere — don't reveal or thrash layout
      cancelAnimationFrame(hoverRaf);
      const { clientX, clientY, target } = e;
      hoverRaf = requestAnimationFrame(() => {
        if (dragging) return;
        const overContainer = updateThumbInteractivity(clientX, clientY);
        if (overContainer) {
          activate(overContainer); // keep it visible while the pointer is on the thumb
          return;
        }
        const el = findScrollableCached(target ?? document.elementFromPoint(clientX, clientY));
        if (el) activate(el);
      });
    };

    let resizeRaf = 0;
    const handleWindowResize = () => {
      cancelAnimationFrame(resizeRaf);
      resizeRaf = requestAnimationFrame(refreshVisible);
    };

    function startDrag(e: PointerEvent, container: Element, axis: Axis, state: Tracked) {
      e.preventDefault();
      const thumb = axis === "v" ? state.vThumb! : state.hThumb!;
      thumb.setPointerCapture(e.pointerId);
      thumb.classList.add("is-dragging");
      if (state.idleTimer) clearTimeout(state.idleTimer);
      const rect = container.getBoundingClientRect();
      dragging =
        axis === "v"
          ? {
              container,
              axis,
              startClient: e.clientY,
              startScroll: container.scrollTop,
              trackLen: rect.height - EDGE_INSET * 2,
              thumbLen: thumb.getBoundingClientRect().height,
              scrollRange: container.scrollHeight - container.clientHeight,
            }
          : {
              container,
              axis,
              startClient: e.clientX,
              startScroll: container.scrollLeft,
              trackLen: rect.width - EDGE_INSET * 2,
              thumbLen: thumb.getBoundingClientRect().width,
              scrollRange: container.scrollWidth - container.clientWidth,
            };

      const onMove = (moveEvent: PointerEvent) => {
        if (!dragging) return;
        const delta = (dragging.axis === "v" ? moveEvent.clientY : moveEvent.clientX) - dragging.startClient;
        const trackFree = dragging.trackLen - dragging.thumbLen;
        const scrollDelta = trackFree > 0 ? (delta / trackFree) * dragging.scrollRange : 0;
        const next = Math.max(0, Math.min(dragging.scrollRange, dragging.startScroll + scrollDelta));
        if (dragging.axis === "v") dragging.container.scrollTop = next;
        else dragging.container.scrollLeft = next;
        updateGeometry(dragging.container);
      };
      const onUp = () => {
        thumb.classList.remove("is-dragging");
        thumb.releasePointerCapture(e.pointerId);
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        dragging = null;
        activate(container);
      };
      window.addEventListener("pointermove", onMove);
      window.addEventListener("pointerup", onUp);
    }

    const sweep = setInterval(() => {
      for (const container of Array.from(tracked.keys())) {
        if (!document.contains(container)) removeTracked(container);
      }
    }, SWEEP_MS);

    window.addEventListener("scroll", handleScroll, { capture: true, passive: true });
    window.addEventListener("pointermove", handlePointerMove, { passive: true });
    window.addEventListener("resize", handleWindowResize);

    return () => {
      window.removeEventListener("scroll", handleScroll, { capture: true });
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("resize", handleWindowResize);
      cancelAnimationFrame(resizeRaf);
      cancelAnimationFrame(hoverRaf);
      clearInterval(sweep);
      for (const container of Array.from(tracked.keys())) removeTracked(container);
    };
  }, []);
}
